/**
 * Diálogo que acompanha os 4 passos da criação e, se parar no meio, mostra o
 * que já existe e deixa tentar de novo — sem duplicar o que já foi criado.
 */

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertCircle, Check, Circle, ExternalLink, Loader2 } from "lucide-react";
import { linkGerenciador } from "@/lib/meta-ads-format";

export type Passos = { campaign: boolean; adset: boolean; creative: boolean; ad: boolean };
const ROTULOS: Array<[keyof Passos, string]> = [
  ["campaign", "Campanha"],
  ["adset", "Conjunto de anúncios"],
  ["creative", "Criativo"],
  ["ad", "Anúncio"],
];

export function ProgressoCriacao({
  open,
  rodando,
  passos,
  erro,
  campaignId,
  adAccountId,
  onRetry,
  onClose,
}: {
  open: boolean;
  rodando: boolean;
  passos: Passos | null;
  erro: { passo: string; message: string } | null;
  campaignId: string | null;
  adAccountId: string | null;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !rodando && onClose()}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => rodando && e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{rodando ? "Criando na Meta…" : erro ? "Parou no meio" : "Criado, pausado"}</DialogTitle>
          <DialogDescription>
            {rodando ? "Quatro passos. Não feche esta janela." : erro ? "O que já foi criado fica guardado; você pode tentar de novo sem duplicar nada." : "Nada entra no ar até você ativar."}
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 text-sm">
          {ROTULOS.map(([k, label]) => {
            const feito = passos?.[k] ?? false;
            const falhou = erro?.passo === k;
            const emCurso = rodando && !feito && !falhou && ROTULOS.findIndex(([kk]) => !(passos?.[kk] ?? false)) === ROTULOS.findIndex(([kk]) => kk === k);
            return (
              <li key={k} className="flex items-center gap-2">
                {feito ? <Check className="h-4 w-4 text-emerald-600" /> : falhou ? <AlertCircle className="h-4 w-4 text-red-600" /> : emCurso ? <Loader2 className="h-4 w-4 animate-spin" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                <span className={feito ? "" : falhou ? "text-red-700" : "text-muted-foreground"}>{label}</span>
              </li>
            );
          })}
        </ul>
        {erro && <p className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">{erro.message}</p>}
        {!rodando && (
          <div className="flex flex-wrap justify-end gap-2">
            {campaignId && (
              <Button variant="outline" size="sm" asChild>
                <a href={linkGerenciador(adAccountId, "campaign", campaignId)} target="_blank" rel="noreferrer">
                  <ExternalLink className="mr-1 h-3 w-3" /> Abrir no Gerenciador
                </a>
              </Button>
            )}
            {erro && <Button size="sm" onClick={onRetry}>Tentar de novo</Button>}
            {!erro && <Button size="sm" onClick={onClose}>Continuar</Button>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
